import { FC, memo } from 'react'

import { colors } from 'styles/colors'

import { addPxUnit, getColor } from '../helpers'

import { IconList } from './constants'
import { IconProps } from './types'

const IconComponent: FC<IconProps> = ({ name, ...props }: IconProps) => {
    const IconComp = IconList?.[name] || IconList.PLACEHOLDER
    return <IconComp {...props} />
}

/**
 *  Component used to render Icons. The current list is available via the variable `IconListType`, exported by the package.
 *  During the bundling process, all SVG Icons are turned into JSX using [SVGR](https://react-svgr.com/docs/rollup/). This component inherits all SVG props like `width`, `height`, `fill`, etc.
 *  If you are searching for the full array of parameters, the React type used is `React.SVGAttributes<SVGSVGElement>`.
 */
const Icon: FC<IconProps> = ({
    name,
    width,
    height,
    size,
    margin = '0',
    rotate = '0',
    color,
    style: styleProp,
    ...props
}) => {
    const computedWidth = addPxUnit(width ?? size ?? '14px')
    const computedHeight = addPxUnit(height ?? size ?? '14px')

    const rotateDeg =
        typeof rotate === 'number' ? rotate : parseInt(String(rotate), 10)
    const safeRotate = Number.isNaN(rotateDeg) ? 0 : rotateDeg

    const svgStyle = {
        minWidth: computedWidth,
        minHeight: computedHeight,
        margin,
        transform: `rotate(${safeRotate}deg)`,
        ...styleProp,
        ...(color ? { color: getColor(color, colors) } : {}),
    }

    return (
        <IconComponent
            name={name}
            role="img"
            aria-label={name}
            width={computedWidth}
            height={computedHeight}
            style={svgStyle}
            {...props}
        />
    )
}

Icon.displayName = 'Icon'
export default memo(Icon)
